import { Dayjs } from "dayjs";
import RepositoryBase, { ResponseEnvelope } from "./RepositoryBase";

import GetTransactionResponse from "./TransactionResponses/GetTransactionResponse";
import GetAllTransactionsResponse from "./TransactionResponses/GetAllTransactionsResponse";
import AddTransactionResponse from "./TransactionResponses/AddTransactionResponse";

export default class TransactionRepository extends RepositoryBase {
  public static async getTransactionById(
    locationId: string,
    transactionId: string
  ): Promise<ResponseEnvelope<GetTransactionResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed())
        return new ResponseEnvelope<GetTransactionResponse>(
          false,
          undefined,
          csrf.errorTitle,
          csrf.errorMessage
        );
      const token = csrf.result!;

      const response = await fetch(
        `${this.protocol}://${this.ip}:${this.port}/api/${locationId}/transaction/${transactionId}`,
        {
          method: "GET",
          mode: "cors",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        }
      );

      if (!response.ok) return this.invalidResponse();

      const data = await response.json();
      if (!data.success) return this.requestUnsuccessful();

      return new ResponseEnvelope<GetTransactionResponse>(
        true,
        data as GetTransactionResponse
      );
    } catch (err) {
      if (err instanceof SyntaxError) {
        return this.invalidFormat();
      } else if (err instanceof TypeError) {
        return this.unableToSendRequest();
      } else {
        console.log(err);
        return this.unexpectedError();
      }
    }
  }

  public static async getAllTransactions(
    locationId: string,
    startDate?: Dayjs,
    endDate?: Dayjs
  ): Promise<ResponseEnvelope<GetAllTransactionsResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed())
        return new ResponseEnvelope<GetAllTransactionsResponse>(
          false,
          undefined,
          csrf.errorTitle,
          csrf.errorMessage
        );
      const token = csrf.result!;

      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate.format("YYYY-MM-DD"));
      if (endDate) params.set("endDate", endDate.format("YYYY-MM-DD"));

      const query = params.toString();
      const url = `${this.protocol}://${this.ip}:${this.port}/api/${locationId}/transaction${
        query ? `?${query}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      });

      if (!response.ok) return this.invalidResponse();

      const data = await response.json();
      if (!data.success) return this.requestUnsuccessful();

      return new ResponseEnvelope<GetAllTransactionsResponse>(
        true,
        data as GetAllTransactionsResponse
      );
    } catch (err) {
      if (err instanceof SyntaxError) {
        return this.invalidFormat();
      } else if (err instanceof TypeError) {
        return this.unableToSendRequest();
      } else {
        console.log(err);
        return this.unexpectedError();
      }
    }
  }

  public static async addTransaction(
    locationId: string,
    amount: number,
    description: string
  ): Promise<ResponseEnvelope<AddTransactionResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed())
        return new ResponseEnvelope<AddTransactionResponse>(
          false,
          undefined,
          csrf.errorTitle,
          csrf.errorMessage
        );
      const token = csrf.result!;

      const response = await fetch(
        `${this.protocol}://${this.ip}:${this.port}/api/${locationId}/transaction`,
        {
          method: "POST",
          mode: "cors",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          body: JSON.stringify({
            amount,
            description,
          }),
        }
      );

      if (!response.ok) return this.invalidResponse();

      const data = await response.json();
      if (!data.success) return this.requestUnsuccessful();

      return new ResponseEnvelope<AddTransactionResponse>(
        true,
        data as AddTransactionResponse
      );
    } catch (err) {
      if (err instanceof SyntaxError) {
        return this.invalidFormat();
      } else if (err instanceof TypeError) {
        return this.unableToSendRequest();
      } else {
        console.log(err);
        return this.unexpectedError();
      }
    }
  }
}
